import {
  type AddMachineOptionDto,
  type MachineAttachmentItemUpdateDto,
  type MachineQuotationItemDto,
  MachinesApiService,
  type QuotationDto
} from '@api/machines'
import type { AddCustomFactoryFitFunctionProps } from '@common/components'
import { handleCatchError, Options, optionChangeType } from '@common/utils'
import { successToast } from '@components/reusable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useParams } from 'react-router-dom'
import { CUSTOMER_SPEC_KEYS } from '@/pages/customers/queries/customerSpecsQueries'
import type { RulesToBeAppliedListType } from '@/pages/customers/utils/interfaces'
import { QUOTE_QUERY_KEYS } from '@/pages/quotation-tool/queries/quoteQueries'
import { HandleOptionChangeParamsType } from '@/pages/quotation-tool/utils/types'
import { useLeaveGuardStore } from '@/stores/leaveGuardStore'
import { QUOTES_QUERIES_KEYS } from '../quotation'

type AddRemoveOptionForSpecItem = {
  attachmentId?: string
  equipmentId?: string
  option: Options
  payload: AddMachineOptionDto
  setRulesToBeApplied?: (rules: RulesToBeAppliedListType) => void
}

export const ATTACHMENTS_QUERY_KEYS = {
  GET_ATTACHMENT_BY_ID: 'getAttachmentById',
  GET_DRAFT_ATTACHMENT: 'getDraftAttachment'
}

export const useGetAttachmentCache = (enableFetch: boolean) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isEditingItem = queryParams.get('edit')
  const { quoteId, specId, equipmentId, attachmentId } = useParams()
  const isNestedAttachment = equipmentId && equipmentId !== 'undefined'
  const isQuoteItem = Boolean(quoteId)
  const isSpecItem = Boolean(specId)

  return useQuery({
    queryKey: [
      ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT,
      isQuoteItem ? quoteId : specId,
      attachmentId
    ],
    queryFn: async () => {
      try {
        if (
          (isQuoteItem && !isNestedAttachment) ||
          (isSpecItem && !isNestedAttachment && !isEditingItem)
        ) {
          // GET Draft Attachment data for Quote Item or Spec Item
          return await MachinesApiService.quotationMachineAttachmentGetDraft(
            String(isQuoteItem ? quoteId : specId),
            String(attachmentId)
          )
        }

        if (isNestedAttachment) {
          // GET Draft Nested Attachment data for Quote or Spec Item
          return await MachinesApiService.quotationMachineMachineAttachmentGetDraft(
            String(isQuoteItem ? quoteId : specId),
            String(equipmentId),
            String(attachmentId)
          )
        }
      } catch (error) {
        handleCatchError(error)
        return null
      }
    },
    refetchOnMount: 'always',
    throwOnError: false,
    enabled: enableFetch
  })
}

export const useGetAttachmentById = (attachmentId: number) => {
  return useQuery({
    queryKey: [ATTACHMENTS_QUERY_KEYS.GET_ATTACHMENT_BY_ID, attachmentId],
    queryFn: async () => {
      try {
        return await MachinesApiService.getMachineAttachmentById(
          attachmentId,
          true
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: Boolean(attachmentId),
    refetchOnMount: 'always'
  })
}

const getOptionCallWithoutRules = (
  option: Options,
  useBrandWarranty?: boolean,
  isWarrantyNull?: boolean
) => {
  // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
  let fnCall
  // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
  let fnCallWithinMachine

  switch (option) {
    case Options.REMOVE_FACTORY_FIT: {
      fnCall = MachinesApiService.quotationMachineAttachmentRemoveFactoryOption
      fnCallWithinMachine = () => null
      break
    }

    case Options.REMOVE_FACTORY_FIT_WITHIN_MACHINE: {
      fnCallWithinMachine =
        MachinesApiService.quotationMachineMachineAttachmentRemoveFactoryFitOption
      fnCall = () => null
      break
    }

    case Options.REMOVE_DEALER_FIT_WITHIN_MACHINE: {
      fnCall = () => null
      fnCallWithinMachine =
        MachinesApiService.quotationMachineMachineAttachmentRemoveDealerFitOption
      break
    }

    case Options.REMOVE_DEALER_FIT: {
      fnCallWithinMachine = () => null
      fnCall =
        MachinesApiService.quotationMachineAttachmentRemoveDealerFitOption
      break
    }

    case Options.ADD_WARRANTY: {
      if (useBrandWarranty) {
        fnCall = MachinesApiService.quotationMachineAttachmentBrandWarranty
      } else {
        fnCall = MachinesApiService.quotationMachineAttachmentAddWarranty
      }
      fnCallWithinMachine = () => null
      break
    }

    case Options.ADD_WARRANTY_WITHIN_MACHINE: {
      if (useBrandWarranty) {
        fnCallWithinMachine =
          MachinesApiService.quotationMachineMachineAttachmentBrandWarranty
      } else {
        fnCallWithinMachine =
          MachinesApiService.quotationMachineMachineAttachmentAddWarranty
      }

      fnCall = () => null
      break
    }

    case Options.REMOVE_WARRANTY: {
      if (useBrandWarranty || isWarrantyNull) {
        fnCall = MachinesApiService.quotationMachineAttachmentBrandWarranty1
      } else {
        fnCall = MachinesApiService.quotationMachineAttachmentRemoveWarranty
      }

      fnCallWithinMachine = () => null
      break
    }

    case Options.REMOVE_WARRANTY_WITHIN_MACHINE: {
      if (useBrandWarranty || isWarrantyNull) {
        fnCallWithinMachine =
          MachinesApiService.quotationMachineMachineAttachmentBrandWarranty1
      } else {
        fnCallWithinMachine =
          MachinesApiService.quotationMachineMachineAttachmentRemoveWarranty
      }

      fnCall = () => null
      break
    }

    case Options.ADD_TRANSPORT_OPTION: {
      fnCall = MachinesApiService.quotationMachineAttachmentAddTransportOption
      fnCallWithinMachine = () => null
      break
    }
    case Options.ADD_TRANSPORT_OPTION_WITHIN_MACHINE: {
      fnCallWithinMachine =
        MachinesApiService.quotationMachineMachineAttachmentAddTransportOption
      fnCall = () => null
      break
    }

    case Options.REMOVE_TRANSPORT_OPTION: {
      fnCall =
        MachinesApiService.quotationMachineAttachmentRemoveTransportOption
      fnCallWithinMachine = () => null
      break
    }

    case Options.REMOVE_TRANSPORT_OPTION_WITHIN_MACHINE: {
      fnCallWithinMachine =
        MachinesApiService.quotationMachineMachineAttachmentRemoveTransportOption
      fnCall = () => null
      break
    }

    default: {
      fnCall = () => null
      fnCallWithinMachine = () => null
      break
    }
  }

  return { fnCall, fnCallWithinMachine }
}

const addFactoryFitForNestedAttachment = async ({
  attachmentId,
  equipmentId,
  option,
  payload,
  setRulesToBeApplied
}: AddRemoveOptionForSpecItem) => {
  const response =
    await MachinesApiService.quotationMachineMachineAttachmentAddFactoryFitOption(
      String(attachmentId),
      payload
    )
  if (response.rulesRequiredChoice) {
    setRulesToBeApplied?.({
      choice: response.rulesRequiredChoice,
      metadata: {
        machineId: equipmentId ?? undefined,
        option: option,
        optionId: Number(payload.optionId)
      }
    })
    return
  }
  return response
}

const addDealerFitForNestedAttachment = async ({
  attachmentId,
  equipmentId,
  option,
  payload,
  setRulesToBeApplied
}: AddRemoveOptionForSpecItem) => {
  const response =
    await MachinesApiService.quotationMachineMachineAttachmentAddDealerFitOption(
      String(attachmentId),
      payload
    )

  if (response.rulesRequiredChoice) {
    setRulesToBeApplied?.({
      choice: response.rulesRequiredChoice,
      metadata: {
        machineId: equipmentId ?? undefined,
        option: option,
        optionId: Number(payload.optionId)
      }
    })
    return
  }
  return response
}

const addFactoryFitForAttachment = async ({
  equipmentId,
  option,
  payload,
  setRulesToBeApplied
}: AddRemoveOptionForSpecItem) => {
  const response =
    await MachinesApiService.quotationMachineAttachmentAddFactoryOption(payload)
  if (response.rulesRequiredChoice) {
    setRulesToBeApplied?.({
      choice: response.rulesRequiredChoice,
      metadata: {
        machineId: equipmentId ?? undefined,
        option: option,
        optionId: Number(payload.optionId)
      }
    })
    return
  }
  return response
}

const addDealerFitForAttachment = async ({
  equipmentId,
  option,
  payload,
  setRulesToBeApplied
}: AddRemoveOptionForSpecItem) => {
  const response =
    await MachinesApiService.quotationMachineAttachmentAddDealerFitOption(
      payload
    )

  if (response.rulesRequiredChoice) {
    setRulesToBeApplied?.({
      choice: response.rulesRequiredChoice,
      metadata: {
        machineId: equipmentId ?? undefined,
        option: option,
        optionId: Number(payload.optionId)
      }
    })
    return
  }
  return response
}

export const useHandleAttachmentOptionChange = ({
  useBrandWarranty,
  setRulesToBeApplied
}: {
  useBrandWarranty?: boolean
  setRulesToBeApplied?: (rules: RulesToBeAppliedListType) => void
} = {}) => {
  const queryClient = useQueryClient()
  const {
    id: customerId,
    quoteId,
    specId,
    equipmentId,
    attachmentId
  } = useParams()
  const queryParams = new URLSearchParams(location.search)
  const isEditing = queryParams.get('edit')
  const isQuoteItem = Boolean(quoteId)
  const isSpecItem = Boolean(specId)

  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )
  // const setRulesToBeApplied = useAttachmentBuilderStore(
  //   state => state.setRulesToBeApplied
  // )

  const isNestedAttachment =
    equipmentId &&
    equipmentId !== 'undefined' &&
    attachmentId &&
    attachmentId !== 'undefined'

  return useMutation({
    mutationFn: async ({
      groupId,
      option,
      optionId
    }: HandleOptionChangeParamsType) => {
      let attachmentItem
      if (isQuoteItem) {
        const cachedData = queryClient.getQueryData([
          ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT,
          isQuoteItem ? quoteId : specId,
          attachmentId
        ]) as MachineAttachmentItemUpdateDto

        const { machineAttachmentItem } = cachedData
        attachmentItem = machineAttachmentItem

        if (typeof machineAttachmentItem?.quotationItemId !== 'string') {
          throw new TypeError('Invalid quotationItemId')
        }
      }

      try {
        const payload: AddMachineOptionDto = {
          itemId: attachmentId,
          optionId,
          quotationId: isQuoteItem ? quoteId : specId,
          ...(groupId && { userOptions: groupId })
        }

        // Add Factory Fit or Dealer Fit for Nested Attachment
        if (isNestedAttachment) {
          Object.assign(payload, {
            itemId: equipmentId
          })

          // Add Factory Fit for Nested Attachment
          if (optionChangeType(option).addFactoryFitForNestedAttachment) {
            return addFactoryFitForNestedAttachment({
              attachmentId,
              equipmentId,
              option,
              payload,
              setRulesToBeApplied
            })
          }

          // Add Dealer Fit for Nested Attachment
          if (optionChangeType(option).addDealerFitForNestedAttachment) {
            return addDealerFitForNestedAttachment({
              attachmentId,
              equipmentId,
              option,
              payload,
              setRulesToBeApplied
            })
          }
        }

        // Add Factory Fit for Standalone Attachment
        if (optionChangeType(option).addFactoryFit) {
          Object.assign(payload, {
            itemId: attachmentId
          })

          return addFactoryFitForAttachment({
            attachmentId,
            equipmentId,
            option,
            payload,
            setRulesToBeApplied
          })
        }

        // Add Dealer Fit for Standalone Attachment
        if (optionChangeType(option).addDealerFit) {
          Object.assign(payload, {
            itemId: attachmentId
          })

          return addDealerFitForAttachment({
            equipmentId,
            option,
            payload,
            setRulesToBeApplied
          })
        }

        const isWarrantyNull = attachmentItem?.warrantyItem === null

        const { fnCall, fnCallWithinMachine } = getOptionCallWithoutRules(
          option,
          useBrandWarranty,
          isWarrantyNull
        )

        if (optionChangeType(option).addOrRemoveForNestedAttachment) {
          if (equipmentId) {
            return await fnCallWithinMachine(
              String(isQuoteItem ? quoteId : specId),
              equipmentId,
              String(attachmentId),
              optionId
            )
          }
        }

        return await fnCall(
          String(isQuoteItem ? quoteId : specId),
          String(attachmentId),
          optionId
        )
      } catch (error) {
        handleCatchError(error)
        return null
      }
    },
    onSuccess: response => {
      if (!response) {
        return
      }

      setHasUnsavedChanges(true)
      successToast()

      if (isQuoteItem && 'machineAttachmentItem' in response) {
        queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
          [
            ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT,
            isQuoteItem ? quoteId : specId,
            attachmentId
          ],
          (oldData?: MachineAttachmentItemUpdateDto) => {
            if (!oldData) {
              return
            }

            return {
              ...oldData,
              machineAttachmentItem: response?.machineAttachmentItem,
              machineAttachmentItemPriceOld:
                response?.machineAttachmentItemPriceOld,
              rulesRequiredChoice: response?.rulesRequiredChoice
            }
          }
        )
      } else if (isSpecItem && 'machineAttachmentItem' in response) {
        const updatedItem =
          response?.machineAttachmentItem as MachineQuotationItemDto

        // When editing an existing Spec Template item the data is read from the
        // SPEC_TEMPLATE query (key: [SPEC_TEMPLATE, specId, customerId, attachmentId]).
        // For Draft attachments (standalone or nested) the data is read from the
        // GET_DRAFT_ATTACHMENT query, whose key is [GET_DRAFT_ATTACHMENT, specId, attachmentId]
        // (no customerId). Writing to the wrong key left the FE stale until reload.
        if (isEditing) {
          // rehydrate cached data for Spec Template Attachment
          queryClient.setQueryData<QuotationDto>(
            [
              CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE,
              specId,
              customerId,
              attachmentId
            ],
            (oldData?: QuotationDto) => {
              if (!oldData) {
                return
              }

              return {
                ...oldData,
                machineAttachmentItems: [updatedItem]
              }
            }
          )
        } else {
          // rehydrate cached data for Draft Attachment (standalone or nested)
          queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
            [ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT, specId, attachmentId],
            (oldData?: MachineAttachmentItemUpdateDto) => {
              if (!oldData) {
                return
              }

              return {
                ...oldData,
                machineAttachmentItem: response?.machineAttachmentItem,
                machineAttachmentItemPriceOld:
                  response?.machineAttachmentItemPriceOld,
                rulesRequiredChoice: response?.rulesRequiredChoice
              }
            }
          )
        }
      }
    },
    throwOnError: false
  })
}

export const useOnSaveAttachment = () => {
  const queryClient = useQueryClient()
  const { quoteId, specId, attachmentId, id: customerId } = useParams()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )
  const isQuoteItem = Boolean(quoteId) && Boolean(attachmentId)

  return useMutation({
    mutationFn: async ({
      specName,
      updateToNewPriceSaving
    }: {
      specName?: string
      updateToNewPriceSaving?: boolean
    }) => {
      try {
        if (isQuoteItem) {
          return await MachinesApiService.quotationMachineAttachmentSaveDraft(
            String(quoteId),
            String(attachmentId),
            updateToNewPriceSaving
          )
        } else {
          sessionStorage.setItem('configSaved', 'true')

          await MachinesApiService.specSaveQuotationMachineAttachmentItemEditedToSpec(
            String(specId),
            String(attachmentId),
            Number(customerId),
            String(specName)
          )
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess: () => {
      setHasUnsavedChanges(false)
      successToast()

      if (isQuoteItem) {
        queryClient.invalidateQueries({
          queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
        })
        queryClient.invalidateQueries({
          queryKey: [QUOTES_QUERIES_KEYS.GET_QUOTATION_MARGIN, quoteId]
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId]
        })
      }
    }
  })
}

export const useOnSaveNestedAttachment = () => {
  const queryClient = useQueryClient()
  const { quoteId, specId, equipmentId, attachmentId } = useParams()

  const isQuoteItem = Boolean(quoteId)

  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async ({
      machineItemId,
      updateToNewPriceSaving
    }: {
      machineItemId?: string
      updateToNewPriceSaving?: boolean
    }) => {
      try {
        if (isQuoteItem) {
          return await MachinesApiService.quotationMachineMachineAttachmentSaveDraft(
            String(quoteId),
            String(equipmentId),
            String(attachmentId),
            updateToNewPriceSaving
          )
        } else {
          sessionStorage.setItem('configSaved', 'true')

          await MachinesApiService.quotationMachineMachineAttachmentSaveDraft(
            String(specId),
            String(machineItemId),
            String(attachmentId),
            updateToNewPriceSaving
          )
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess: () => {
      setHasUnsavedChanges(false)
      successToast()

      if (isQuoteItem) {
        queryClient.invalidateQueries({
          queryKey: [QUOTE_QUERY_KEYS.QUOTE, quoteId]
        })
        queryClient.invalidateQueries({
          queryKey: [QUOTES_QUERIES_KEYS.GET_QUOTATION_MARGIN, quoteId]
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId]
        })
      }
    }
  })
}

export const useUpdateAttachment = () => {
  const queryClient = useQueryClient()
  const { specId, attachmentId } = useParams()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async ({
      specId,
      customerId,
      templateId
    }: {
      specId: string
      customerId: number
      templateId: string
    }) => {
      try {
        return await MachinesApiService.customersUpdateSpecTemplateEdited(
          specId,
          customerId,
          templateId,
          specId,
          String(attachmentId)
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess: () => {
      setHasUnsavedChanges(false)
      successToast()

      queryClient.invalidateQueries({
        queryKey: [ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT, specId]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId]
      })
    }
  })
}

export const useHandleUpdateAttachmentNotes = () => {
  const { quoteId, specId, equipmentId, attachmentId } = useParams()

  const queryClient = useQueryClient()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  const isQuoteItem = Boolean(quoteId)
  const isNestedAttachment = equipmentId && equipmentId !== 'undefined'

  return useMutation({
    mutationFn: async (notes: string) => {
      try {
        if (isNestedAttachment) {
          return await MachinesApiService.quotationMachineMachineAttachmentUpdateNotes(
            String(isQuoteItem ? quoteId : specId),
            String(equipmentId),
            String(attachmentId),
            notes
          )
        }

        return await MachinesApiService.quotationMachineAttachmentUpdateNotes(
          String(isQuoteItem ? quoteId : specId),
          String(attachmentId),
          notes
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess: () => {
      setHasUnsavedChanges(true)
      successToast()

      if (isQuoteItem) {
        queryClient.invalidateQueries({
          queryKey: [
            ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT,
            isQuoteItem ? quoteId : specId
          ]
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId]
        })
      }
    }
  })
}

export const useUpdateAttachmentDealerFitQuantity = () => {
  const queryClient = useQueryClient()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isEditingItem = queryParams.get('edit')
  const { quoteId, specId, equipmentId, attachmentId } = useParams()

  const isNestedAttachment = equipmentId && equipmentId !== 'undefined'
  const isQuoteItem = Boolean(quoteId)
  const isSpecItem = Boolean(specId)

  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async ({
      actionType,
      optionId,
      itemQuantity
    }: {
      actionType: 'decrease' | 'increase'
      optionId: number
      itemQuantity: number
    }) => {
      let auxQuantity = itemQuantity
      switch (actionType) {
        case 'increase':
          auxQuantity += 1
          break
        case 'decrease':
          auxQuantity -= 1
          break
        default:
          break
      }

      try {
        return await MachinesApiService.quotationMachineAttachmentUpdateDealerFitOptionQuantity(
          String(isQuoteItem ? quoteId : specId),
          String(attachmentId),
          optionId,
          auxQuantity,
          isNestedAttachment ? equipmentId : undefined
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess: () => {
      setHasUnsavedChanges(true)
      successToast()

      queryClient.invalidateQueries({
        queryKey: [
          ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT,
          isQuoteItem ? quoteId : specId
        ]
      })

      if (isSpecItem && isEditingItem) {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId]
        })
      }
    }
  })
}

export const useClearAttachmentCache = () => {
  const queryClient = useQueryClient()
  const { specId, attachmentId } = useParams()

  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  return useMutation({
    mutationFn: async () => {
      try {
        await MachinesApiService.quotationMachineAttachmentClearDraft(
          String(attachmentId)
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess: () => {
      successToast()

      if (specId) {
        queryClient.invalidateQueries({
          queryKey: [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId]
        })
      }

      setHasUnsavedChanges(false)
    }
  })
}

export const useAddCustomFactoryFitAttachment = () => {
  const queryClient = useQueryClient()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isEditingItem = queryParams.get('edit')
  const {
    id: customerId,
    quoteId,
    specId,
    equipmentId,
    attachmentId
  } = useParams()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  const isQuoteItem = Boolean(quoteId)
  const isSpecItem = Boolean(specId)
  const isNestedAttachment = equipmentId && equipmentId !== 'undefined'

  return useMutation({
    mutationFn: async ({
      isUpdating,
      name,
      optionId,
      dnp
    }: AddCustomFactoryFitFunctionProps) => {
      const saveWithinMachine = isUpdating
        ? MachinesApiService.quotationMachineMachineAttachmentUpdateCustomFactoryFitOption
        : MachinesApiService.quotationMachineMachineAttachmentAddCustomFactoryOption

      const saveStandAlone = isUpdating
        ? MachinesApiService.quotationMachineAttachmentUpdateCustomFactoryOption
        : MachinesApiService.quotationMachineAttachmentAddCustomFactoryOption

      try {
        // nested attachment save function
        if (isNestedAttachment) {
          return await saveWithinMachine({
            dnp,
            attachmentItemId: attachmentId,
            machineItemId: equipmentId,
            name,
            optionId,
            quotationId: isQuoteItem ? quoteId : specId
          })
        }

        // stand alone attachment save function
        return await saveStandAlone({
          dnp,
          attachmentItemId: attachmentId,
          name,
          optionId,
          quotationId: isQuoteItem ? quoteId : specId
        })
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess(response) {
      setHasUnsavedChanges(true)
      successToast()

      if (isQuoteItem) {
        queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
          [ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT, quoteId, attachmentId],
          oldData => {
            if (!oldData) {
              return
            }

            return {
              ...oldData,
              machineAttachmentItem: response?.machineAttachmentItem,
              machineAttachmentItemPriceOld:
                response?.machineAttachmentItemPriceOld,
              rulesRequiredChoice: response?.rulesRequiredChoice
            }
          }
        )
      }

      if (isSpecItem) {
        if (isNestedAttachment || !isEditingItem) {
          queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
            [ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT, specId, attachmentId],
            oldData => {
              if (!oldData) {
                return
              }

              return {
                ...oldData,
                machineAttachmentItem: response?.machineAttachmentItem,
                machineAttachmentItemPriceOld:
                  response?.machineAttachmentItemPriceOld,
                rulesRequiredChoice: response?.rulesRequiredChoice
              }
            }
          )
        } else {
          // rehydrate cached data for Spec Template Attachment
          queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
            [
              CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE,
              specId,
              customerId,
              attachmentId
            ],
            oldData => {
              if (!oldData) {
                return
              }

              return {
                ...oldData,
                machineAttachmentItems: [response?.machineAttachmentItem]
              }
            }
          )
        }
      }
    }
  })
}

export const useAddCustomDealerFitAttachment = () => {
  const queryClient = useQueryClient()
  const {
    id: customerId,
    quoteId,
    specId,
    equipmentId,
    attachmentId
  } = useParams()
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  const isQuoteItem = Boolean(quoteId)
  const isSpecItem = Boolean(specId)
  const isNestedAttachment = equipmentId && equipmentId !== 'undefined'

  return useMutation({
    mutationFn: async ({
      isUpdating,
      name,
      optionId,
      dnp
    }: AddCustomFactoryFitFunctionProps) => {
      const saveWithinMachine = isUpdating
        ? MachinesApiService.quotationMachineMachineAttachmentUpdateCustomDealerOption
        : MachinesApiService.quotationMachineMachineAttachmentAddCustomDealerOption
      const saveStandAlone = isUpdating
        ? MachinesApiService.quotationMachineAttachmentUpdateCustomDealerOption
        : MachinesApiService.quotationMachineAttachmentAddCustomDealerOption

      try {
        // nested attachment save function
        if (isNestedAttachment) {
          return await saveWithinMachine({
            attachmentItemId: attachmentId,
            machineItemId: equipmentId,
            dnp,
            name,
            optionId,
            quotationId: isQuoteItem ? quoteId : specId
          })
        }

        // stand alone attachment save function
        return await saveStandAlone({
          attachmentItemId: attachmentId,
          dnp,
          name,
          optionId,
          quotationId: isQuoteItem ? quoteId : specId
        })
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    onSuccess(response) {
      setHasUnsavedChanges(true)
      successToast()

      if (isQuoteItem) {
        queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
          [ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT, quoteId, attachmentId],
          oldData => {
            if (!oldData) {
              return
            }

            return {
              ...oldData,
              machineAttachmentItem: response?.machineAttachmentItem,
              machineAttachmentItemPriceOld:
                response?.machineAttachmentItemPriceOld,
              rulesRequiredChoice: response?.rulesRequiredChoice
            }
          }
        )
      }

      if (isSpecItem) {
        if (isNestedAttachment) {
          // rehydrate cached data for Nested Attachment
          queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
            [ATTACHMENTS_QUERY_KEYS.GET_DRAFT_ATTACHMENT, specId, attachmentId],
            oldData => {
              if (!oldData) {
                return
              }

              return {
                ...oldData,
                machineAttachmentItem: response?.machineAttachmentItem,
                machineAttachmentItemPriceOld:
                  response?.machineAttachmentItemPriceOld,
                rulesRequiredChoice: response?.rulesRequiredChoice
              }
            }
          )
        } else {
          // rehydrate cached data for Stand Alone Attachment
          queryClient.setQueryData<MachineAttachmentItemUpdateDto>(
            [
              CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE,
              specId,
              customerId,
              attachmentId
            ],
            oldData => {
              if (!oldData) {
                return
              }

              return {
                ...oldData,
                machineAttachmentItems: [response?.machineAttachmentItem]
              }
            }
          )
        }
      }
    }
  })
}
